import React, { useEffect, useCallback, useMemo, useRef } from 'react';
import _ from 'lodash';
import PropTypes from 'prop-types';
import { useTranslator } from '@jutro/locale';
import { useModal } from '@jutro/components';
import { messages as commonMessages } from '@xengage/gw-platform-translations';
import { WMICOneIncPaymentService } from 'wmic-capability-quoteandbind';
import { HasFeatureService }  from 'gw-capability-quoteandbind';
import { publish } from '@jutro/events';
import { WMICTopics } from 'wmic-portals-utils-js';
import appConfig from 'app-config';

import messages from './WMICOneIncPaymentModalQnB.messages';

const WMICOneIncPaymentModalQnB = (props) => {
    const modalApi = useModal();

    const {
        paymentModalLoaded,
        paymentModalClosed, 
        saveComplete, 
        paymentComplete, 
        paymentError,
        paymentDetails, 
        payNowEnabled,
        savePaymentEnabled
    } = props;

    const translator = useTranslator();
    const portalOne = useRef(null);
    let saveModal = null;
    let oneInc = null;
    const modalLoadedRef = useRef(false);
    const pendingPaymentRef = useRef(null);
    const launchPaymentIfReady = () => {
        if(portalOne.current && pendingPaymentRef.current){
            portalOne.current.makePayment(pendingPaymentRef.current);
            pendingPaymentRef.current = null;
        }
    }
    const { oneIncSettings } = appConfig;
    const applicationFeatures = HasFeatureService.getApplicationFeatures();
    const agentquote = applicationFeatures.agentquoteandbind === 'true' ;
    const errorMessages = useMemo(() => {
        return {
            'default': {
                redirectState: 'contactUs',
                redirectStateIsErrorPage: true,
                title: messages.cannotProceed,// POR-2061
                message: messages.cannotProceedMsg,
                description: agentquote ? 'quoteandbind.controllers.WizardFlowCtrl.underwriters.' +
                'Sorry, based on the data entered this quote cannot be completed online.'
                    : 'quoteandbind.controllers.WizardFlowCtrl.Sorry, based on the data entered this quote cannot be completed online.'
            },
            'DM': {
                redirectState: 'contactUs',
                redirectStateIsErrorPage: true,
                title: messages.cannotProceed,// POR-2061
                message: messages.cannotProceedMsg,
                description: 'quoteandbind.controllers.WizardFlowCtrl.DM Issue.'
            },
            'UWIssue': {
                redirectState: 'contactUs',
                redirectStateIsErrorPage: true,
                title: messages.contactUs,// POR-2061
                message: messages.contactUs,
                description: 'Hello, given the information provided we would like to personally discuss your available options within our range of insurance products.'+
                'At your earliest convenience, please give our Quote Specialists a call at 800-640-2920 to explore the benefits of W Insurance today.'+
                '(Call Center Business Hours: Monday - Friday 8:00am - 5:00pm).'
            },
            'CLUEReport': {
                redirectState: 'contactUs',
                redirectStateIsErrorPage: true,
                title: messages.contactUs,// POR-2061
                message: messages.contactUs,
                description: 'quoteandbind.controllers.WizardFlowCtrl.Hi there! Based on the information you\'ve provided, we\'d like to personally discuss your insurance options'
            },
            'PortalValidation': {
                redirectState: 'contactUs',
                redirectStateIsErrorPage: true,
                title: messages.contactUs,// POR-2061
                message: messages.contactUs,
                description: 'quoteandbind.controllers.WizardFlowCtrl.Hi there! Based on the information you\'ve provided, we\'d like to personally discuss your insurance options'
            },
            'PaymentError': {
                redirectState: 'contactUs',
                redirectStateIsErrorPage: true,
                title: messages.paymentDetailsFailure
            },
            'ConsentError': {
                redirectState: 'contactUs',
                redirectStateIsErrorPage: false,
                title: messages.paymentUnsuccessful,
                message: messages.paymentUnsuccessfulMsg
            },
            'AuthorizationError': {
                redirectState: 'policyInProcess',
                redirectStateIsErrorPage: true,
                title: messages.authorizationFailure
            },
            'DayTransitionError': {
                redirectState: 'policyInProcess',
                redirectStateIsErrorPage: true,
                title: messages.weHaveUpdated
            },
            'TwelvePayBindError': {
                redirectState: 'policyInProcess',
                redirectStateIsErrorPage: true,
                title: messages.confirmationOfPurchase
            },
            'InvalidSession': {
                redirectState: 'contactUs',
                redirectStateIsErrorPage: true
            },
            'PaymentSystemDown': {
                redirectState: 'contactUs',
                redirectStateIsErrorPage: false,
                title: messages.paymentSystemUnavailable,
                message: messages.paymentSystemUnavailableMsg
            }
        }
    }, [agentquote])

    const handleError = useCallback((error) => {
        let errorTitle = errorMessages.default.title;
        let errorMsg = errorMessages.default.message;
        let redirectPage = errorMessages.default.redirectState;
        if (error?.code === "PaymentError") {
            errorTitle = (errorMessages[error.code] || errorMessages.default).title;
            errorMsg = error.paymentErrors;
            redirectPage = (errorMessages[error.code] || errorMessages.default).redirectState;
        } else if (error?.length > 0 && error[0].validationExceptionType === "PaymentError") {
            errorTitle = (errorMessages[error[0].validationExceptionType] || errorMessages.default).title;
            errorMsg = error[0].issueMessage;
            redirectPage = (errorMessages[error[0].validationExceptionType] || errorMessages.default).redirectState;
        } 
        modalLoadedRef.current = true; // modal has loaded - in error though
        modalApi.showAlert({
            title: translator(errorTitle),
            message: translator(errorMsg),
            status: 'error',
            icon: 'mi-error-outline',
            confirmButtonText: translator(commonMessages.close)
        }).finally(() => paymentError(redirectPage));
    }, [errorMessages, paymentError, translator]);

    const onLoad = (e) => {
        console.log('[OneINC-QnB] onLoad event - Full detail object:', JSON.stringify(e?.detail, null, 2));
    }

    const onUnload = () => {
        console.log('[OneINC-QnB] onUnload event');
        paymentModalClosed();
    }

    const onError = (e) => {
        console.log('[OneINC-QnB] onError event:', e?.detail);
        // TODO: use appropriate logger
        console.log(`error querying availability:${ e.detail }`);
    }

    const onPaymentComplete = (e) => {
        console.log('[OneINC-QnB] onPaymentComplete event:', e?.detail);
        paymentComplete({details: {
            detail: e.detail,
            saveModal: saveModal
        }});
    }

    const onSaveComplete = (e) => {
        console.log('[OneINC-QnB] onSaveComplete event:', e?.detail);
        saveComplete({details: e.detail});
    }

    const onPaymentCanceled = (e) => {
        console.log('[OneINC-QnB] onPaymentCanceled event:', e?.detail);
        if (e.srcElement) {
            e.srcElement.innerHTML = '';
        }
        paymentModalClosed();
    }

    const onModalDialogOpened = () => {
        console.log('[OneINC-QnB] onModalDialogOpened event');
        modalLoadedRef.current = true;
        paymentModalLoaded();
        
        // Track payment modal opening for Adobe Analytics
        const quoteId = _.get(paymentDetails, 'submission.quoteID.value');
        const state = _.get(paymentDetails, 'submission.baseData.policyAddress.value.state');
        
        publish(WMICTopics.ROUTE_CHANGED, {
            page: 'insurance:renters:quote:payment paymentmethodmodal',
            subchannel2: 'quote',
            ins_quoteid: quoteId,
            ins_quote_id: quoteId ? 'IQQ' + quoteId : '',
            state: state
        });
    }

    const initEventHandlers = useCallback((container) => {
        if (!container) {
            return;
        }

        console.log('[OneINC-QnB] Initializing event handlers on container:', container);
        
        // Standard OneINC events
        container.addEventListener('portalOne.load', onLoad);
        container.addEventListener('portalOne.unload', onUnload);
        container.addEventListener('portalOne.error', onError);
        container.addEventListener('portalOne.paymentComplete', onPaymentComplete);
        container.addEventListener('portalOne.saveComplete', onSaveComplete);
        container.addEventListener('portalOne.paymentCanceled', onPaymentCanceled);
        container.addEventListener('portalOne.modalDialogOpened', onModalDialogOpened);
        
        // Debug: Log ALL OneINC events
        const debugEvents = [
            'portalOne.screenChanged',
            'portalOne.navigationChanged',
            'portalOne.contentLoaded',
            'portalOne.ready',
            'portalOne.formSubmitted',
            'portalOne.stepChanged'
        ];
        
        debugEvents.forEach(eventName => {
            container.addEventListener(eventName, (e) => {
                console.log(`[OneINC-QnB-DEBUG] ${eventName}:`, e?.detail);
            });
        });
        
        console.log('[OneINC-QnB] Event listeners registered');
    }, []);

    useEffect(() => {
        let mounted = true;
        let container = null;

        const handleOneIncReady = () => {
            if (!mounted) return;
            // eslint-disable-next-line react-hooks/exhaustive-deps
            oneInc = window.OneInc;
            container = document.getElementById('portalOneContainer');
            console.log('[OneINC-QnB] Initializing PortalOne with container:', container);
            portalOne.current = new oneInc.PortalOne(container, initEventHandlers);
            launchPaymentIfReady();
        };
        const handleOneIncFailed = () => {
            if (!mounted) return;
            handleError();
        };

        if (window.__oneIncReady || window.OneInc?.PortalOne) {
            handleOneIncReady();
        } else if (window.__oneIncFailed) {
            handleOneIncFailed();
        } else {
            window.addEventListener('oneIncReady', handleOneIncReady);
            window.addEventListener('oneIncFailed', handleOneIncFailed);
        }

        return () => {
            mounted = false;
            window.removeEventListener('oneIncReady', handleOneIncReady);
            window.removeEventListener('oneIncFailed', handleOneIncFailed);
            console.log('[OneINC-QnB] Cleaning up event listeners');
            if (container) {
                container.removeEventListener('portalOne.load', onLoad);
                container.removeEventListener('portalOne.unload', onUnload);
                container.removeEventListener('portalOne.error', onError);
                container.removeEventListener('portalOne.paymentComplete', onPaymentComplete);
                container.removeEventListener('portalOne.saveComplete', onSaveComplete);
                container.removeEventListener('portalOne.paymentCanceled', onPaymentCanceled);
                container.removeEventListener('portalOne.modalDialogOpened', onModalDialogOpened);
            }
        };
    }, []);

    const removeHttp = (url) => {
        return url ? url.replace(/^https?:\/\//, '') : '';
    };

    const modalLoadTimeout = useCallback(() => {
        setTimeout( () => {
            if (!modalLoadedRef.current) {
                handleError();
            }
        }, oneIncSettings.modalLoadTimeOut);
    }, [handleError, oneIncSettings.modalLoadTimeOut]);

    const getFeeTypeDetailsDTO = (pmtDetails) => {
        return {
            quoteID: _.get(pmtDetails, 'submission.quoteID.value')
        };
    };

    const getOneIncSessionDetailsDTO = (pmtDetails) => {
        return {
            bankAccountFirstName: pmtDetails.bankAccountFirstName,
            bankAccountLastName: pmtDetails.bankAccountLastName,
            payerPublicID: pmtDetails.payerPublicID,
            quoteID: _.get(pmtDetails, 'submission.quoteID.value'),
            sessionID: _.get(pmtDetails, 'submission.sessionUUID.value')
        };
    };

    // find where is oneIncPayment comming from
    useEffect(() => {
        if(payNowEnabled) {
            // this below is the payNow function:
            modalLoadTimeout();
            let paymentCategory;
            switch (paymentDetails.paymentType) {
                case 'credit':
                    paymentCategory = 'CreditCard';
                    break;
                case 'bank':
                    paymentCategory = 'ECheck';
                    break;
                default:
                    paymentCategory = 'CreditCard';
                    break;
            }
            // Store the saveModal so it can get passed back when payment is complete
            saveModal = paymentDetails.saveModal;

            const feeTypeDetailsDTO = getFeeTypeDetailsDTO(paymentDetails);
            const oneIncSessionDetailsDTO = getOneIncSessionDetailsDTO(paymentDetails);
            const billingAddressStreet = paymentDetails.billingAddress ? `${paymentDetails.billingAddress.addressLine1  }, ${  paymentDetails.billingAddress.city  }, ${  paymentDetails.billingAddress.state}` : '';
            const billingZip = paymentDetails.billingAddress ? paymentDetails.billingAddress.postalCode : '';

            WMICOneIncPaymentService.getFeeType(feeTypeDetailsDTO).then((result) => {
                if (!result.feeType) {
                    handleError(result.systemMessages);

                    return;
                }
            });

            WMICOneIncPaymentService.getOneIncSessionDetails(oneIncSessionDetailsDTO).then((result) => {
                if (!result.oneIncSessionID) {
                    handleError(result.systemMessages);
                    return;
                }
                const paymentObject = {
                    'sessionId': result.oneIncSessionID,
                    'paymentCategory': paymentCategory,
                    'feeContext': 'PaymentWithFee',
                    'convenienceFeeType': result.feeTypeValueOneInc_Ext,
                    'minAmountDue': paymentDetails.paymentAmount,
                    'accountBalance': paymentDetails.paymentAmount,
                    'amountContext': 'AmountDueOnly',
                    'billingZip': billingZip,
                    'billingAddressStreet': billingAddressStreet,
                    'policyHolderName': paymentDetails.userDisplayName,
                    'confirmationDisplay': 'true',
                    'saveOption': 'DoNotSave',
                    'allowClosing': 'true',
                    'displayMode': 'Modal',
                    'email': paymentDetails.email,
                    'clientReferenceData1': paymentDetails.invoiceStream,
                    'clientReferenceData2': paymentDetails.submission.quoteID.value,
                    'clientReferenceData4': 'QNB',
                    'clientReferenceData5': removeHttp(result?.pcUrl)
                };

                pendingPaymentRef.current = paymentObject;
                launchPaymentIfReady();
            }, (error) => {
                handleError(error);
            }).catch((error) => {
                handleError(error);
            });
        }
    }, [payNowEnabled])


    const savePaymentMethod = useCallback((pmtDetails) => {
        modalLoadTimeout();
        const oneIncSessionDetailsDTO = getOneIncSessionDetailsDTO(paymentDetails);

        WMICOneIncPaymentService.getOneIncSessionDetails(oneIncSessionDetailsDTO).then((result) => {
            if (!result.oneIncSessionID) {
                handleError(result.systemMessages);
                return;
            }
            const savePaymentObj = {
                customerId: result.oneIncCustomerID,
                sessionId: result.oneIncSessionID,
                acceptCreditCards: 'false',
                acceptPrepaidCards: 'false',
                paymentCategory: 'ECheck',
                policyHolderName: pmtDetails.userDisplayName,
                allowClosing: 'true',
                displayMode: 'Modal',
                email: pmtDetails.email,
                clientReferenceData2: pmtDetails.submission.quoteID.value,
                clientReferenceData4: 'QNB',
                clientReferenceData5: removeHttp(result.pcUrl)
            };

            portalOne.current.savePaymentMethod(savePaymentObj);
        }, (error) => {
            handleError(error);
        });
    }, [handleError, modalLoadTimeout, paymentDetails]);

    useEffect(() => {
        if(savePaymentEnabled){
            savePaymentMethod(paymentDetails);
        }
    }, [savePaymentEnabled, paymentDetails, savePaymentMethod]);

    return (
        <div id="portalOneContainer"/>
    );
};

WMICOneIncPaymentModalQnB.propTypes = {
    paymentDetails: PropTypes.shape({}).isRequired,
    paymentModalClosed: PropTypes.func.isRequired,
    saveComplete: PropTypes.func.isRequired,
    paymentModalLoaded: PropTypes.func.isRequired,
    payNowEnabled: PropTypes.bool.isRequired,
    savePaymentEnabled: PropTypes.bool.isRequired,
    paymentError: PropTypes.func.isRequired
};

WMICOneIncPaymentModalQnB.defaultProps = {
    paymentComplete: () => {},
    paymentModalLoaded: () => {},
    paymentError: () => {}
};

export default WMICOneIncPaymentModalQnB;